/**
 * AdMesh Tracker
 *
 * Handles MRC-compliant exposure tracking for recommendations
 *
 * MRC Viewability Standards:
 * - Display Ads: 50% of pixels visible for at least 1 continuous second
 * - Large Display Ads (>242,500 pixels): 30% of pixels visible for at least 1 continuous second
 */

import { logger } from '../utils/logger';

export interface TrackerConfig {
  apiKey: string;
  debug?: boolean;
}

/**
 * MRC Viewability threshold configuration
 */
interface MRCThreshold {
  visibilityPercentage: number;  // 50% for standard ads, 30% for large ads
  minimumDurationMs: number;     // 1000ms (1 second)
}

export class AdMeshTracker {
  private firedExposures: Set<string> = new Set();
  private debug: boolean = false;
  private mrcThreshold: MRCThreshold = {
    visibilityPercentage: 50,
    minimumDurationMs: 1000
  };

  constructor(config: TrackerConfig) {
    this.debug = config.debug || false;
  }

  /**
   * Fire an exposure tracking pixel with MRC viewability compliance
   *
   * This method should be called when an ad element becomes viewable according to MRC standards.
   * The caller is responsible for ensuring the ad meets the MRC threshold before calling this method.
   *
   * Prevents duplicate firing for the same ad in the same session.
   *
   * @param exposureUrl - The tracking pixel URL to fire
   * @param recommendationId - The recommendation ID for deduplication
   * @param sessionId - The session ID for deduplication
   */
  fireExposure(exposureUrl: string, recommendationId: string, sessionId: string): void {
    const key = `${sessionId}_${recommendationId}`;

    // Prevent duplicate exposures
    if (this.firedExposures.has(key)) {
      if (this.debug) {
        logger.log('[Tracker] Exposure already fired');
      }
      return;
    }

    this.firedExposures.add(key);

    try {
      // Fire the exposure pixel
      // Note: The caller should ensure MRC compliance before calling this method
      fetch(exposureUrl, { method: 'GET', keepalive: true }).catch(() => {
        if (this.debug) {
          logger.warn('[Tracker] Failed to fire exposure');
        }
      });

      if (this.debug) {
        logger.log('[Tracker] Fired MRC-compliant exposure');
      }
    } catch (error) {
      if (this.debug) {
        logger.error('[Tracker] Error firing exposure');
      }
    }
  }

  /**
   * Fire an exposure pixel with MRC viewability verification
   *
   * This method monitors an element for MRC viewability compliance before firing the exposure pixel.
   * It uses Intersection Observer API to track visibility and ensures the ad meets the threshold
   * (50% visible for 1 continuous second) before firing.
   *
   * @param exposureUrl - The tracking pixel URL to fire
   * @param recommendationId - The recommendation ID for deduplication
   * @param sessionId - The session ID for deduplication
   * @param element - The DOM element to monitor for viewability
   * @returns Promise that resolves when exposure is fired or timeout occurs
   */
  async fireExposureWithMRCCompliance(
    exposureUrl: string,
    recommendationId: string,
    sessionId: string,
    element: HTMLElement
  ): Promise<void> {
    const key = `${sessionId}_${recommendationId}`;

    // Prevent duplicate exposures
      if (this.firedExposures.has(key)) {
        if (this.debug) {
          logger.log('[Tracker] MRC exposure already fired');
        }
        return;
      }

    return new Promise((resolve) => {
      let viewableStartTime: number | null = null;
      let timeoutId: NodeJS.Timeout | null = null;

      const observer = new IntersectionObserver(
        (entries) => {
          entries.forEach((entry) => {
            const visibilityPercentage = (entry.intersectionRatio * 100);

            if (visibilityPercentage >= this.mrcThreshold.visibilityPercentage) {
              // Ad is visible enough
              if (viewableStartTime === null) {
                // Start tracking viewable duration
                viewableStartTime = Date.now();

                if (this.debug) {
                  logger.log('[Tracker] Ad reached MRC visibility threshold');
                }

                // Set timeout to fire exposure after minimum duration
                timeoutId = setTimeout(() => {
                  // Fire the exposure pixel
                  this.fireExposure(exposureUrl, recommendationId, sessionId);
                  observer.disconnect();
                  resolve();
                }, this.mrcThreshold.minimumDurationMs);
              }
            } else {
              // Ad visibility dropped below threshold
              if (viewableStartTime !== null) {
                // Reset tracking
                if (timeoutId) {
                  clearTimeout(timeoutId);
                  timeoutId = null;
                }
                viewableStartTime = null;

                if (this.debug) {
                  logger.log('[Tracker] Ad visibility dropped below MRC threshold');
                }
              }
            }
          });
        },
        {
          threshold: [0, 0.1, 0.2, 0.3, 0.4, 0.5, 0.6, 0.7, 0.8, 0.9, 1.0],
          rootMargin: '0px'
        }
      );

      observer.observe(element);

      // Cleanup on unmount or after reasonable timeout
      const maxWaitTime = 30000; // 30 seconds max wait
      const cleanupTimeout = setTimeout(() => {
        observer.disconnect();
        if (timeoutId) {
          clearTimeout(timeoutId);
        }
        resolve();
      }, maxWaitTime);

      // Store cleanup function for manual cleanup if needed
      (element as any).__admeshTrackerCleanup = () => {
        observer.disconnect();
        if (timeoutId) {
          clearTimeout(timeoutId);
        }
        clearTimeout(cleanupTimeout);
      };
    });
  }

  /**
   * Clear fired exposures (useful for testing or session reset)
   */
  clearFiredExposures(): void {
    this.firedExposures.clear();
  }

  /**
   * Get MRC threshold configuration
   */
  getMRCThreshold(): MRCThreshold {
    return { ...this.mrcThreshold };
  }

  /**
   * Set custom MRC threshold (for testing or special cases)
   */
  setMRCThreshold(threshold: Partial<MRCThreshold>): void {
    this.mrcThreshold = {
      ...this.mrcThreshold,
      ...threshold
    };
  }

  /**
   * Fire exposure pixel for sponsored followup
   * Uses same logic as regular exposure tracking
   * 
   * @param exposureUrl - The tracking pixel URL to fire (can use regular exposure_url)
   * @param recommendationId - The recommendation ID for deduplication
   * @param sessionId - The session ID for deduplication
   */
  fireFollowupExposure(
    exposureUrl: string,
    recommendationId: string,
    sessionId: string
  ): void {
    // Reuse existing fireExposure() method
    this.fireExposure(exposureUrl, recommendationId, sessionId);
  }

  /**
   * Fire engagement tracking for sponsored followup
   * 
   * @param engagementUrl - The engagement tracking URL to fire
   * @param recommendationId - The recommendation ID
   * @param sessionId - The session ID (sent in POST body for analytics joining)
   * @returns Promise that resolves when engagement is fired
   */
  fireFollowupEngagement(
    engagementUrl: string,
    _recommendationId: string,
    sessionId: string
  ): Promise<void> {
    // Use fetch with keepalive and POST method
    // Always resolve the promise even on error to prevent unhandled rejections
    return fetch(engagementUrl, {
      method: 'POST',
      keepalive: true,
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ session_id: sessionId })
    }).catch((error) => {
      // Log error silently (only in debug mode) - network errors are expected and shouldn't break the flow
      if (this.debug) {
        logger.warn('[Tracker] Failed to fire followup engagement (non-critical):', error);
      }
      // Return undefined to resolve the promise successfully
      // This prevents the error from propagating to callers
      return undefined;
    }).then(() => {
      // Ensure promise always resolves with void (no return value)
      // This makes the return type consistent: Promise<void>
    });
  }
}
